import React, { useState } from 'react';
import { Archive, RefreshCw, Trash2, Search, X, ShieldAlert, CheckCircle } from 'lucide-react';
import { Lead } from '../types';

interface ArchiveRecoveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  archivedLeads: Lead[];
  onRestoreLead: (id: string) => void;
  onPermanentDeleteLead: (id: string) => void;
}

export default function ArchiveRecoveryModal({
  isOpen,
  onClose,
  archivedLeads,
  onRestoreLead,
  onPermanentDeleteLead,
}: ArchiveRecoveryModalProps) {
  const [search, setSearch] = useState('');

  if (!isOpen) return null;

  const filtered = archivedLeads.filter(
    (l) =>
      (l.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.company || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.owner || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-amber-500/10 dark:bg-amber-950/30">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-amber-500 text-white shadow-xs">
              <Archive className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-slate-900 dark:text-slate-100 text-base">
                Archive Recovery Center (Trash Bin)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Archived leads are kept here safely for recovery or audit purposes.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search archived records by name, company, or owner..."
              className="w-full pl-9 pr-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-amber-500"
            />
          </div>
          <span className="text-xs font-bold text-slate-500 bg-slate-200 dark:bg-slate-800 px-2.5 py-1 rounded-md">
            {filtered.length} Archived {filtered.length === 1 ? 'Record' : 'Records'}
          </span>
        </div>

        {/* Archived Leads Table */}
        <div className="p-5 flex-1 overflow-y-auto space-y-2">
          {filtered.length === 0 ? (
            <div className="p-10 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/30">
              <Archive className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
              <p className="text-sm font-bold text-slate-600 dark:text-slate-400">
                No archived leads found.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Leads archived from the main dashboard table will appear here for recovery.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              {filtered.map((lead) => (
                <div
                  key={lead.id}
                  className="p-3.5 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition flex flex-wrap items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                        {lead.company || lead.name}
                      </span>
                      {lead.accountNumber && (
                        <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                          #{lead.accountNumber}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-1">
                      <span>Contact: {lead.contactPerson || lead.name || 'N/A'}</span>
                      <span>•</span>
                      <span>Owner: {lead.owner || 'Unassigned'}</span>
                      <span>•</span>
                      <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                        ${(lead.estimatedDealValue || lead.dealValue || 0).toLocaleString()}/mo
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onRestoreLead(lead.id)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/80 border border-emerald-300 dark:border-emerald-800 hover:bg-emerald-100 transition cursor-pointer"
                      title="Restore lead back to active pipeline board"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Restore
                    </button>
                    <button
                      onClick={() => onPermanentDeleteLead(lead.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition cursor-pointer"
                      title="Permanently remove record from database"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/80 flex items-center justify-between">
          <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> All records can be safely restored at any time.
          </span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-300 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
